// Adding a lead, and editing one: a centred dialog with the essentials up front — business name, phone, city, area, priority
// and the contact's name — and everything else behind "More options". The phone is read as you type with the same function
// the server uses, so the number you will get (`+91…`) is shown before saving, and a landline is called out (no WhatsApp).
//
// The form sends only what a person can fix: a business name left empty, a phone that is not an Indian number, a rating or
// a count that is not a number. A `400` puts each field error under the field it names, opening "More options" when the
// field is in there; `409` (another lead has the phone) goes under the phone with the server's own words.

import type { CreateLead, Lead, LeadPriority, UpdateLead } from '@clavian/api-client';
import { normalizeIndianPhone } from '@clavian/domain/leads';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, FormSection, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type Control, type FieldPath } from 'react-hook-form';

import { PROBLEM, problemCode, problemDetail } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { placeFieldErrors } from '../organizations/owner';
import { LEAD_PRIORITIES, ratingText } from './labels';
import { useCreateLead, useLeadAssignees, useUpdateLead } from './queries';

export interface LeadFormValues {
  readonly businessName: string;
  readonly phone: string;
  readonly city: string;
  readonly area: string;
  readonly priority: LeadPriority;
  readonly contactName: string;
  readonly ownerUserId: string;
  readonly email: string;
  readonly instagram: string;
  readonly addressHint: string;
  readonly stationCount: string;
  readonly platforms: string;
  readonly currentSoftware: string;
  readonly googleRating: string;
  readonly googleReviews: string;
  readonly mapUrl: string;
  readonly hook: string;
}

type FormPath = FieldPath<LeadFormValues>;

/** The select's stand-in for "nobody": an option cannot have an empty value. */
const NO_OWNER = 'none';

const EMPTY: LeadFormValues = {
  businessName: '',
  phone: '',
  city: '',
  area: '',
  priority: 'c',
  contactName: '',
  ownerUserId: '',
  email: '',
  instagram: '',
  addressHint: '',
  stationCount: '',
  platforms: '',
  currentSoftware: '',
  googleRating: '',
  googleReviews: '',
  mapUrl: '',
  hook: '',
};

/** The fields behind "More options": a refusal that names one opens it. */
const MORE_FIELDS: ReadonlySet<string> = new Set(['ownerUserId', 'email', 'instagram', 'addressHint', 'stationCount', 'platforms', 'currentSoftware', 'googleRating', 'googleReviews', 'mapUrl', 'hook']);

/** The api's field names that this form has a field for, and that field. */
const API_FIELDS: Readonly<Record<string, FormPath>> = {
  businessName: 'businessName',
  phone: 'phone',
  city: 'city',
  area: 'area',
  priority: 'priority',
  contactName: 'contactName',
  ownerUserId: 'ownerUserId',
  email: 'email',
  instagram: 'instagram',
  addressHint: 'addressHint',
  stationCount: 'stationCount',
  platforms: 'platforms',
  currentSoftware: 'currentSoftware',
  googleRatingTenths: 'googleRating',
  googleReviewCount: 'googleReviews',
  mapUrl: 'mapUrl',
  hook: 'hook',
};

/** The form's values for a lead being edited. */
export function valuesOfLead(lead: Lead): LeadFormValues {
  return {
    businessName: lead.businessName,
    phone: lead.phone ?? '',
    city: lead.city ?? '',
    area: lead.area ?? '',
    priority: lead.priority,
    contactName: lead.contactName ?? '',
    ownerUserId: lead.ownerUserId ?? '',
    email: lead.email ?? '',
    instagram: lead.instagram ?? '',
    addressHint: lead.addressHint ?? '',
    stationCount: lead.stationCount === null ? '' : String(lead.stationCount),
    platforms: lead.platforms.join(', '),
    currentSoftware: lead.currentSoftware ?? '',
    googleRating: lead.googleRatingTenths === null ? '' : ratingText(lead.googleRatingTenths),
    googleReviews: lead.googleReviewCount === null ? '' : String(lead.googleReviewCount),
    mapUrl: lead.mapUrl ?? '',
    hook: lead.hook ?? '',
  };
}

/** A rating as typed (`4.6`, `5`) in tenths, or `undefined` when it is not one from 0 to 5 with at most one decimal. */
export function ratingTenthsOf(text: string): number | undefined {
  if (!/^\d(\.\d)?$/.test(text.trim())) return undefined;
  const tenths = Math.round(Number(text) * 10);
  return tenths >= 0 && tenths <= 50 ? tenths : undefined;
}

const wholeNumber = (text: string): number | undefined => (/^\d{1,9}$/.test(text.trim()) ? Number(text) : undefined);

type Problem = 'phone' | 'rating' | 'stationCount' | 'googleReviews';

/**
 * The request from what was typed. Creating leaves an empty optional field out; editing sends `null` for it, so clearing a
 * field clears it. The phone goes as the number the server will store.
 */
export function leadBodyOf(values: LeadFormValues, editing: boolean): { readonly body: CreateLead & UpdateLead; readonly problems: readonly Problem[] } {
  const problems: Problem[] = [];
  const out: Record<string, unknown> = { businessName: values.businessName.trim(), priority: values.priority };
  const text = (key: string, value: string) => {
    const trimmed = value.trim();
    if (trimmed !== '') out[key] = trimmed;
    else if (editing) out[key] = null;
  };
  const phone = values.phone.trim();
  if (phone !== '') {
    const read = normalizeIndianPhone(phone);
    if (read === null) problems.push('phone');
    else out['phone'] = read.e164;
  } else if (editing) out['phone'] = null;
  text('city', values.city);
  text('area', values.area);
  text('contactName', values.contactName);
  text('email', values.email);
  text('instagram', values.instagram);
  text('addressHint', values.addressHint);
  text('currentSoftware', values.currentSoftware);
  text('mapUrl', values.mapUrl);
  text('hook', values.hook);
  if (values.stationCount.trim() !== '') {
    const count = wholeNumber(values.stationCount);
    if (count === undefined) problems.push('stationCount');
    else out['stationCount'] = count;
  } else if (editing) out['stationCount'] = null;
  if (values.googleReviews.trim() !== '') {
    const count = wholeNumber(values.googleReviews);
    if (count === undefined) problems.push('googleReviews');
    else out['googleReviewCount'] = count;
  } else if (editing) out['googleReviewCount'] = null;
  if (values.googleRating.trim() !== '') {
    const tenths = ratingTenthsOf(values.googleRating);
    if (tenths === undefined) problems.push('rating');
    else out['googleRatingTenths'] = tenths;
  } else if (editing) out['googleRatingTenths'] = null;
  const platforms = values.platforms
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '');
  if (platforms.length > 0 || editing) out['platforms'] = platforms;
  if (values.ownerUserId !== '') out['ownerUserId'] = values.ownerUserId;
  else if (editing) out['ownerUserId'] = null;
  return { body: out as unknown as CreateLead & UpdateLead, problems };
}

export interface LeadFormDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The lead being edited; omit to add one. */
  readonly lead?: Lead | undefined;
  /** Called with the lead once it is saved, before the dialog closes. */
  readonly onSaved?: (lead: Lead) => void;
}

export function LeadFormDialog({ open, onOpenChange, lead, onSaved }: LeadFormDialogProps): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" dirty="auto" title={lead === undefined ? t('leads.form.createTitle') : t('leads.form.editTitle')} description={lead?.businessName}>
      {open ? <LeadForm lead={lead} onSaved={onSaved} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function LeadForm({ lead, onSaved, onDone }: { readonly lead: Lead | undefined; readonly onSaved: ((lead: Lead) => void) | undefined; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const formId = useId();
  const editing = lead !== undefined;
  const create = useCreateLead();
  const update = useUpdateLead();
  const assignees = useLeadAssignees();
  const form = useForm<LeadFormValues>({ defaultValues: lead === undefined ? EMPTY : valuesOfLead(lead) });
  const [failure, setFailure] = useState<ReactNode>(undefined);
  const [moreOpen, setMoreOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const saving = create.isPending || update.isPending;
  const phoneText = useWatch({ control: form.control, name: 'phone' });
  const ownerUserId = useWatch({ control: form.control, name: 'ownerUserId' });
  const priority = useWatch({ control: form.control, name: 'priority' });
  const read = phoneText.trim() === '' ? undefined : normalizeIndianPhone(phoneText);

  // After a refusal, take the reader to the first field that names it.
  useEffect(() => {
    if (failure === undefined) return;
    formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [failure]);

  const phoneDescription =
    phoneText.trim() === '' ? t('leads.form.phoneHint') : read === null ? t('leads.form.phoneInvalid') : `${t('leads.form.phoneNormalised', { phone: read?.e164 ?? '' })}${read !== undefined && !read.mobile ? ` ${t('leads.form.phoneLandline')}` : ''}`;

  const submit = form.handleSubmit(async (values) => {
    setFailure(undefined);
    const place = (name: FormPath, message: string) => {
      form.setError(name, { type: 'server', message });
      if (MORE_FIELDS.has(name)) setMoreOpen(true);
    };
    const { body, problems: found } = leadBodyOf(values, editing);
    if (values.businessName.trim() === '') place('businessName', t('leads.form.businessRequired'));
    if (found.includes('phone')) place('phone', t('leads.form.phoneInvalid'));
    if (found.includes('rating')) place('googleRating', t('leads.form.ratingInvalid'));
    if (found.includes('stationCount')) place('stationCount', t('leads.form.numberInvalid'));
    if (found.includes('googleReviews')) place('googleReviews', t('leads.form.numberInvalid'));
    if (found.length > 0 || values.businessName.trim() === '') {
      setFailure(t('leads.form.failed'));
      return;
    }
    try {
      const saved = lead === undefined ? await create.mutateAsync(body) : await update.mutateAsync({ leadId: lead.id, body });
      show({ tone: 'ok', title: t(lead === undefined ? 'leads.form.created' : 'leads.form.saved', { name: saved.businessName }) });
      onSaved?.(saved);
      onDone();
    } catch (error) {
      if (problemCode(error) === PROBLEM.leadPhoneTaken) {
        place('phone', problemDetail(error) ?? t('leads.form.phoneTaken'));
        setFailure(t('leads.form.failed'));
      } else if (problemCode(error) === PROBLEM.validationFailed) {
        const unplaced = placeFieldErrors(error, API_FIELDS, (name, message) => place(name as FormPath, message));
        setFailure(unplaced.length > 0 ? unplaced.join(' ') : t('leads.form.failed'));
      } else {
        setFailure(problems.text(error, { fallback: t('leads.form.failed') }));
      }
    }
  });

  return (
    <form id={formId} ref={formRef} className="console-form console-hq-leadform" noValidate onSubmit={(event) => void submit(event)}>
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <FormSection
        forceOpen={moreOpen}
        moreLabel={t('leads.form.more')}
        lessLabel={t('leads.form.less')}
        more={
          <>
            <SelectField
              label={t('leads.form.owner')}
              options={[{ value: NO_OWNER, label: t('leads.form.noOwner') }, ...(assignees.data?.data ?? []).map((one) => ({ value: one.id, label: one.name }))]}
              value={ownerUserId || NO_OWNER}
              onValueChange={(value) => form.setValue('ownerUserId', value === NO_OWNER ? '' : value, { shouldDirty: true })}
            />
            <Txt control={form.control} name="email" label={t('leads.form.email')} type="email" autoComplete="off" />
            <Txt control={form.control} name="instagram" label={t('leads.form.instagram')} description={t('leads.form.instagramHint')} spellCheck={false} />
            <Txt control={form.control} name="addressHint" label={t('leads.form.addressHint')} />
            <div className="console-hq-leadform__pair">
              <Txt control={form.control} name="stationCount" label={t('leads.form.stationCount')} inputMode="numeric" />
              <Txt control={form.control} name="currentSoftware" label={t('leads.form.currentSoftware')} />
            </div>
            <Txt control={form.control} name="platforms" label={t('leads.form.platforms')} description={t('leads.form.platformsHint')} />
            <div className="console-hq-leadform__pair">
              <Txt control={form.control} name="googleRating" label={t('leads.form.googleRating')} description={t('leads.form.googleRatingHint')} inputMode="decimal" />
              <Txt control={form.control} name="googleReviews" label={t('leads.form.googleReviews')} inputMode="numeric" />
            </div>
            <Txt control={form.control} name="mapUrl" label={t('leads.form.mapUrl')} inputMode="url" spellCheck={false} />
            <Txt control={form.control} name="hook" label={t('leads.form.hook')} description={t('leads.form.hookHint')} />
          </>
        }
      >
        <Txt control={form.control} name="businessName" label={t('leads.form.businessName')} required />
        <Txt control={form.control} name="phone" label={t('leads.form.phone')} description={phoneDescription} inputMode="tel" autoComplete="off" />
        <div className="console-hq-leadform__pair">
          <Txt control={form.control} name="city" label={t('leads.form.city')} />
          <Txt control={form.control} name="area" label={t('leads.form.area')} />
        </div>
        <div className="console-hq-leadform__pair">
          <SelectField
            label={t('leads.form.priority')}
            description={t('leads.form.priorityHint')}
            options={LEAD_PRIORITIES.map((priority) => ({ value: priority, label: t(`leads.priority.${priority}`) }))}
            value={priority}
            onValueChange={(value) => form.setValue('priority', value as LeadPriority, { shouldDirty: true })}
          />
          <Txt control={form.control} name="contactName" label={t('leads.form.contactName')} />
        </div>
      </FormSection>
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('leads.form.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={saving}>
          {t(editing ? 'leads.form.save' : 'leads.form.create')}
        </Button>
      </div>
    </form>
  );
}

function Txt({
  control,
  name,
  label,
  description,
  required = false,
  ...input
}: {
  readonly control: Control<LeadFormValues>;
  readonly name: FormPath;
  readonly label: string;
  readonly description?: string;
  readonly required?: boolean;
  readonly type?: string;
  readonly inputMode?: 'numeric' | 'decimal' | 'tel' | 'url';
  readonly autoComplete?: string;
  readonly spellCheck?: boolean;
}): ReactNode {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <TextField {...input} label={label} description={description} required={required} name={field.name} value={String(field.value)} onChange={field.onChange} onBlur={field.onBlur} error={fieldState.error?.message} />
      )}
    />
  );
}
